/**
 * Pre-send gauntlet — shared validator + consent flow invoked by every
 * "send a package" affordance in the portal.
 *
 * Steps:
 *   1. Per-signer payment-amount client-side validation (min $0.50, max $5,000)
 *   2. Branding disclosure soft warning if companyName / paperCopyContact missing
 *   3. Instant Bank Payments offer / reconcile dialog (conditional on payments
 *      configured AND the platform feature is on for this tenant)
 *
 * Returns { ok: true } if all steps cleared and the caller should proceed
 * with the actual send/schedule API call. Returns { ok: false } on any abort.
 *
 * Why a function module (not a composable): the consumers are Options-API
 * Vue components, this stays framework-neutral, and the IBP dialog is mounted
 * as a ref-based component in each consumer so we avoid prop-drilling state.
 *
 * Callers wire by:
 *   1. Mounting <IbtOfferDialog ref="ibtDialog" /> in their template
 *   2. Importing this module + brandingApi
 *   3. Calling runPreSendGauntletAsync({ ... ctx ... }) before send
 */

import { branding as brandingApi } from '@services/signApi.js';

/**
 * Run the pre-send gauntlet.
 *
 * @param {Object} ctx
 * @param {Array}  ctx.signers   - [{ name, email, paymentAmount, routingOrder }]
 * @param {Boolean} ctx.enforceSigningOrder
 * @param {Boolean} ctx.enableInstantBankTransfer
 * @param {Boolean} ctx.paymentCollectionEnabled
 * @param {Object} ctx.$q        - Quasar plugin (for $q.dialog + $q.notify)
 * @param {Object} ctx.ibtDialogRef - ref to IbtOfferDialog component
 * @param {Function} ctx.persistIbtChangeAsync - async (newEnableInstantBankTransfer: bool) => bool
 *                                              Caller persists the flip via wizard
 *                                              saveBasicInfo OR direct packages.update PATCH.
 *                                              Must return false to abort the send if persist failed.
 * @param {Function} [ctx.onEditSigners] - optional. If provided, called when the
 *                                         sender chooses "Edit signers" outcome.
 *                                         Use to jump to the signers tab/step.
 * @returns {Promise<{ ok: boolean }>}
 */
export async function runPreSendGauntletAsync(ctx) {
    const {
        signers = [],
        enforceSigningOrder,
        enableInstantBankTransfer,
        paymentCollectionEnabled,
        $q,
        ibtDialogRef,
        persistIbtChangeAsync,
        onEditSigners
    } = ctx;

    // Step 1: per-signer payment amount min/max
    for (const signer of signers) {
        if (signer.paymentAmount > 0) {
            if (signer.paymentAmount < 0.50) {
                $q.notify({ type: 'negative', message: `Payment amount for ${signer.name} must be at least $0.50.` });
                return { ok: false };
            }
            if (signer.paymentAmount > 5000) {
                $q.notify({ type: 'negative', message: `Payment amount for ${signer.name} cannot exceed $5,000.` });
                return { ok: false };
            }
        }
    }

    // Step 2: branding disclosure soft warning
    try {
        const brandingResp = await brandingApi.get();
        const b = brandingResp.data || {};
        const warnings = [];
        if (!b.companyName) warnings.push('<li><strong>Company name</strong> is not set. The signing disclosure will use generic "Sending Party" language instead of your company name.</li>');
        if (!b.disclosurePaperCopyContact) warnings.push('<li><strong>Paper copy contact</strong> is not set. Signers who request paper copies will be directed to "the contact information in your signing invitation" instead of a specific contact method.</li>');

        if (warnings.length > 0) {
            const proceed = await new Promise(resolve => {
                $q.dialog({
                    title: 'Branding Setup Recommended',
                    html: true,
                    message: `
                        <p style="margin-bottom: 12px;">Federal law requires an electronic signing disclosure for every signer.
                        Your disclosure is missing some personalization:</p>
                        <ul style="margin: 0 0 12px 0; padding-left: 20px;">${warnings.join('')}</ul>
                        <p style="margin-bottom: 0;">You can configure these anytime at <strong>Settings &rarr; Branding &rarr; Signing Disclosure</strong>.
                        The package will still send with generic defaults.</p>`,
                    cancel: { label: 'Go to Settings', flat: true },
                    ok: { label: 'Send Anyway', color: 'primary' },
                    persistent: true
                }).onOk(() => resolve(true)).onCancel(() => resolve(false));
            });
            if (!proceed) {
                // Plan §6.5 — open Branding in a new tab so the sender keeps
                // the package open and can return to send when they're done.
                // The prior `window.location.href` threw away the send context
                // with no breadcrumb back.
                window.open('/Settings/Branding', '_blank', 'noopener');
                return { ok: false };
            }
        }
    } catch {
        // Branding check failed — proceed anyway (non-blocking).
    }

    // Step 3: IBP offer / reconcile (conditional)
    const ibtOutcome = await maybeRunIbtDialog({
        signers,
        enforceSigningOrder,
        enableInstantBankTransfer,
        paymentCollectionEnabled,
        ibtDialogRef
    });

    if (ibtOutcome === 'edit') {
        if (typeof onEditSigners === 'function') onEditSigners();
        return { ok: false };
    }
    if (ibtOutcome === 'accept' || ibtOutcome === 'disable') {
        const newValue = ibtOutcome === 'accept';
        const persisted = await persistIbtChangeAsync(newValue);
        if (!persisted) {
            $q.notify({
                type: 'negative',
                message: 'Could not update Instant Bank Payments setting. Try again.'
            });
            return { ok: false };
        }
    }

    return { ok: true };
}

/**
 * Classifies package state + opens the IBP dialog if relevant.
 *
 * Returns: 'skip' | 'accept' | 'disable' | 'decline' | 'edit'.
 *
 * Exported so toggle-driven entry points (PackagesBuilder IBP toggle, etc.)
 * can invoke just the IBP-classification step without the payment-config /
 * branding-disclosure pre-checks that only matter at send-time. Same mode
 * classification, same dialog component, same outcome vocabulary.
 */
export async function classifyAndRunIbtDialogAsync(ctx) {
    return await maybeRunIbtDialog(ctx);
}

async function maybeRunIbtDialog({
    signers,
    enforceSigningOrder,
    enableInstantBankTransfer,
    paymentCollectionEnabled,
    ibtDialogRef
}) {
    // Skip if the tenant can't collect payments at all.
    if (!paymentCollectionEnabled) return 'skip';

    const payers = (signers || []).filter(s => s.paymentAmount && s.paymentAmount > 0);
    const ibtOn = !!enableInstantBankTransfer;

    // Skip if IBP off + no payments configured — IBP isn't relevant here.
    if (!ibtOn && payers.length === 0) return 'skip';

    // Evaluate invariant compliance for the current package state.
    const allSigners = signers || [];
    const maxOrder = allSigners.length > 0
        ? Math.max(...allSigners.map(s => s.routingOrder ?? 0))
        : 0;
    const primary = payers.length > 0
        ? [...payers].sort((a, b) => (a.routingOrder ?? 0) - (b.routingOrder ?? 0))[0]
        : null;
    const hasMultiplePayers = payers.length > 1;
    const payerNotLast = primary && (primary.routingOrder ?? 0) !== maxOrder;
    const orderOff = !enforceSigningOrder;

    // Skip if IBP is already on AND all invariants are satisfied —
    // smooth template-derived path, no dialog interrupts the send.
    if (ibtOn && payers.length === 1 && !payerNotLast && !orderOff) {
        return 'skip';
    }

    // Compute the planned remediation diff for fixup / reconcile-fixup modes.
    const changes = [];
    if (primary && orderOff) {
        changes.push('Turn on sequential signing order.');
    }
    if (primary && payerNotLast) {
        changes.push(`Move ${primary.name || primary.email} to the last signer position.`);
    }
    const payerNames = payers.map(p => p.name || p.email);

    // Choose dialog mode.
    //   reconcile-empty   — IBP currently on, no payer left. Offer to disable.
    //   reconcile-conflict — IBP currently on, multiple payers. Offer to disable
    //                        (post-review fix P2.1, plan §2.1 — prior `conflict`
    //                        mode here returned 'decline' which sent card-only
    //                        THIS time but left IBP flag set, causing the same
    //                        dialog to re-fire on every subsequent send).
    //   conflict          — IBP off, multiple payers configured. Same UX
    //                        (edit or send card-only), but 'decline' is correct
    //                        because there's nothing to persist.
    //   reconcile-fixup   — IBP on, 1 payer, invariants broken. Fix or disable.
    //   fixup             — IBP off, 1 payer, fix + enable.
    //   offer             — IBP off, 1 payer, invariants OK. Clean Yes/No.
    let mode;
    if (ibtOn && payers.length === 0) {
        mode = 'reconcile-empty';
    } else if (ibtOn && hasMultiplePayers) {
        mode = 'reconcile-conflict';
    } else if (hasMultiplePayers) {
        mode = 'conflict';
    } else if (changes.length > 0) {
        mode = ibtOn ? 'reconcile-fixup' : 'fixup';
    } else {
        mode = 'offer';
    }

    if (!ibtDialogRef || typeof ibtDialogRef.open !== 'function') {
        // Defensive: caller didn't wire the dialog ref. Surface as Decline so
        // the send still proceeds (caller will hit server-side validation for
        // any genuine violation).
        // eslint-disable-next-line no-console
        console.warn('preSendGauntlet: ibtDialogRef not provided; skipping IBP dialog.');
        return 'decline';
    }

    return await ibtDialogRef.open({
        mode,
        plannedChanges: changes,
        payerNames,
        signerCount: allSigners.length
    });
}
